import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  gameDetailPath,
  collectionDetailPath,
  postDetailPath,
} from "../../utils/routes";
import styles from "./UserProfilePanel.module.css";
import { post } from "../../utils/request";
import {
  getCachedPreviewUrl,
  getFilePreviewUrl,
  getFilePreviewUrlList,
} from "../../utils/attachmentPreview";
import FollowButton from "../FollowButton/FollowButton";
import { useToast } from "../Toast/Toast";
import { t, noAutoTranslateUserTextProps } from "../../utils/i18n";
import { stripHtml } from "../../utils/stripHtml";
import {
  TRACE_TYPE_USER,
  USER_PROFILE_FILTER_COLLECTIONS,
  USER_PROFILE_FILTER_GAMES,
  USER_PROFILE_FILTER_TOPICS,
  extractLinkedCollectionsFromPosts,
  firstLinkedCollectionId,
  firstLinkedGameId,
  firstLinkedGameName,
  postMatchesUserProfileFilter,
  resolveUserDisplayName,
} from "../../utils/userProfile";
import {
  ensureCurrentUserId,
  getCachedCurrentUserId,
} from "../../utils/currentUser";

const POST_PAGE_SIZE = 20;
const COLLECTION_PAGE_SIZE = 20;
const COLLECTIONS_API = "/game/collection/collection-list";

const FILTER_OPTIONS = [
  {
    id: USER_PROFILE_FILTER_GAMES,
    labelKey: "userProfile.filter.games",
    fallback: "Games",
  },
  {
    id: USER_PROFILE_FILTER_TOPICS,
    labelKey: "userProfile.filter.topics",
    fallback: "Topics",
  },
  {
    id: USER_PROFILE_FILTER_COLLECTIONS,
    labelKey: "userProfile.filter.collections",
    fallback: "Collections",
  },
];

const unwrapData = res => {
  const raw = res?.data ?? res;
  if (raw && typeof raw === "object" && raw.data != null) {
    return raw.data;
  }
  return raw;
};

const unwrapList = res => {
  const raw = res?.data ?? res;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  return [];
};

const firstChar = text => {
  const s = String(text ?? "").trim();
  return s ? s.charAt(0).toUpperCase() : "?";
};

const normalizeCollectionItem = item => {
  const id =
    item?.id ??
    item?.collectionId ??
    item?.code ??
    item?.collectionCode ??
    "";
  const iconId =
    item?.collectionIcon != null && item.collectionIcon !== ""
      ? String(item.collectionIcon)
      : "";
  return {
    id,
    collectionName: item?.collectionName || item?.name || "",
    collectionIntro:
      item?.collectionIntro ||
      item?.description ||
      item?.summary ||
      item?.introduction ||
      "",
    collectionIcon: getCachedPreviewUrl(iconId) || iconId,
    gameSize: Number(item?.gameSize) || 0,
    iconId,
  };
};

const normalizePostItem = item => ({
  ...item,
  attachmentUUID: item?.attachmentUUID ?? item?.attachmentUuid ?? "",
  attachmentImageUrls: [],
  liked: !!(item?.liked ?? item?.isLiked ?? item?.isThumbed),
});

const resolveAvatarFromRaw = async raw => {
  if (!raw) return "";
  const isLikelyId =
    typeof raw === "string" && raw.length > 0 && !raw.startsWith("http");
  if (isLikelyId) {
    const url = await getFilePreviewUrl(raw);
    return url || "";
  }
  return raw;
};

/** 合并同一 userId 的并发加载（含 React StrictMode 双次 effect） */
const profileBundleInflight = new Map();

const fetchUserProfileBundle = async (
  userId,
  isLoggedIn,
  { fallbackName, fallbackAvatarUrl },
) => {
  let currentUserId = null;
  let following = false;
  let followingCount = 0;
  let followersCount = 0;
  let displayName = fallbackName.trim();
  let introduction = "";
  let avatarUrl = fallbackAvatarUrl.trim();
  let resolvedUserId = userId;
  let posts = [];
  let collections = [];

  let selfId = getCachedCurrentUserId();
  if (isLoggedIn) {
    if (selfId != null) {
      currentUserId = selfId;
    } else {
      try {
        const meId = await ensureCurrentUserId();
        if (meId != null) {
          selfId = meId;
          currentUserId = meId;
        }
      } catch {
        /* ignore */
      }
    }
  }

  const accountRes = await post(`/user/account/info/${userId}`, {});
  const account = unwrapData(accountRes);
  if (account) {
    const name = resolveUserDisplayName(account, fallbackName);
    if (name) displayName = name;
    const id = account?.id ?? userId;
    if (id != null) resolvedUserId = id;
    const avatarRaw = account?.avatarUrl || fallbackAvatarUrl;
    const resolved = await resolveAvatarFromRaw(avatarRaw);
    if (resolved) avatarUrl = resolved;
    if (account?.introduction) {
      introduction = String(account.introduction).trim();
    }
  } else if (fallbackAvatarUrl) {
    const resolved = await resolveAvatarFromRaw(fallbackAvatarUrl);
    if (resolved) avatarUrl = resolved;
  }

  try {
    const statsRes = await post("/user/trace/userTraceStats", {
      userId: Number(resolvedUserId) || resolvedUserId,
    });
    const stats = unwrapData(statsRes);
    followingCount = stats?.followingCount ?? 0;
    followersCount = stats?.followersCount ?? 0;
  } catch {
    /* ignore */
  }

  const viewingSelf = selfId != null && String(selfId) === String(userId);
  if (isLoggedIn && !viewingSelf) {
    try {
      const traceRes = await post("/user/trace/list", { type: TRACE_TYPE_USER });
      const traceData = unwrapData(traceRes);
      const followedUsers = Array.isArray(traceData?.user) ? traceData.user : [];
      following = followedUsers.some(
        item => String(item?.id ?? item?.userId ?? "") === String(userId),
      );
    } catch {
      /* ignore */
    }
  }

  try {
    const postsRes = await post("/social/article/list", {
      data: { userId: Number(userId) || userId },
      pageNo: 1,
      pageSize: POST_PAGE_SIZE,
      sortingFieldList: [],
    });
    posts = unwrapList(postsRes).map(normalizePostItem);
  } catch (postError) {
    console.warn("Failed to load user posts:", postError);
  }

  try {
    const collectionsRes = await post(COLLECTIONS_API, {
      data: {
        userId: Number(userId) || userId,
        createBy: String(userId),
      },
      pageNo: 1,
      pageSize: COLLECTION_PAGE_SIZE,
      sortingFieldList: [],
    });
    collections = unwrapList(collectionsRes).map(normalizeCollectionItem);
  } catch (collectionError) {
    console.warn("Failed to load user collections:", collectionError);
  }

  return {
    currentUserId,
    following,
    followingCount,
    followersCount,
    introduction,
    displayName,
    avatarUrl,
    resolvedUserId,
    posts,
    collections,
  };
};

const loadUserProfileBundleOnce = (userId, isLoggedIn, options) => {
  const key = `${userId}:${isLoggedIn ? 1 : 0}`;
  if (profileBundleInflight.has(key)) {
    return profileBundleInflight.get(key);
  }
  const promise = fetchUserProfileBundle(userId, isLoggedIn, options).finally(
    () => {
      profileBundleInflight.delete(key);
    },
  );
  profileBundleInflight.set(key, promise);
  return promise;
};

/**
 * Desktop user profile modal — opened from comment avatars, aligned with Android UserProfileBinder.
 */
const UserProfilePanel = ({
  userId,
  fallbackName = "",
  fallbackAvatarUrl = "",
  isLoggedIn,
  onRequireLogin,
  onClose,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const toastRef = useRef(toast);
  const attachmentFetchRef = useRef(new Set());
  const collectionIconFetchRef = useRef(new Set());

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState(fallbackName.trim());
  const [introduction, setIntroduction] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(fallbackAvatarUrl.trim());
  const [resolvedUserId, setResolvedUserId] = useState(userId);
  const [currentUserId, setCurrentUserId] = useState(() =>
    isLoggedIn ? getCachedCurrentUserId() : null,
  );
  const [following, setFollowing] = useState(() =>
    isLoggedIn ? null : false,
  );
  const [followingCount, setFollowingCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(USER_PROFILE_FILTER_GAMES);
  const [posts, setPosts] = useState([]);
  const [collections, setCollections] = useState([]);

  const isSelf = useMemo(() => {
    if (!currentUserId || !resolvedUserId) return false;
    return String(currentUserId) === String(resolvedUserId);
  }, [currentUserId, resolvedUserId]);

  const showFollowButton = useMemo(() => {
    if (!isLoggedIn) return true;
    if (currentUserId == null) return false;
    if (isSelf) return false;
    return following !== null;
  }, [isLoggedIn, currentUserId, isSelf, following]);

  const filteredPosts = useMemo(
    () => posts.filter(item => postMatchesUserProfileFilter(item, selectedFilter)),
    [posts, selectedFilter],
  );

  const visibleCollections = useMemo(() => {
    if (collections.length > 0) return collections;
    return extractLinkedCollectionsFromPosts(posts);
  }, [collections, posts]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    attachmentFetchRef.current = new Set();
    collectionIconFetchRef.current = new Set();
    setLoading(true);
    setDisplayName(fallbackName.trim());
    setIntroduction("");
    setAvatarUrl(fallbackAvatarUrl.trim());
    setResolvedUserId(userId);
    setFollowingCount(0);
    setFollowersCount(0);
    if (isLoggedIn) {
      setCurrentUserId(getCachedCurrentUserId());
      setFollowing(null);
    } else {
      setCurrentUserId(null);
      setFollowing(false);
    }

    loadUserProfileBundleOnce(userId, isLoggedIn, {
      fallbackName,
      fallbackAvatarUrl,
    })
      .then(bundle => {
        if (cancelled || !bundle) return;
        if (bundle.currentUserId != null) {
          setCurrentUserId(bundle.currentUserId);
        }
        setFollowing(bundle.following);
        setFollowingCount(bundle.followingCount ?? 0);
        setFollowersCount(bundle.followersCount ?? 0);
        setIntroduction(bundle.introduction ?? "");
        setDisplayName(bundle.displayName);
        setAvatarUrl(bundle.avatarUrl);
        setResolvedUserId(bundle.resolvedUserId);
        setPosts(bundle.posts);
        setCollections(bundle.collections);
      })
      .catch(error => {
        if (cancelled) return;
        console.warn("Failed to load user profile:", error);
        toastRef.current?.error?.(
          error?.message ||
            t("userProfile.error.load", "Failed to load user profile"),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isLoggedIn]);

  useEffect(() => {
    posts.forEach(item => {
      const id = String(item.id);
      if (attachmentFetchRef.current.has(id)) return;
      if (Array.isArray(item.attachmentImageUrls) && item.attachmentImageUrls.length > 0) {
        attachmentFetchRef.current.add(id);
        return;
      }
      if (item?.attachments?.[0]?.previewUrl) {
        attachmentFetchRef.current.add(id);
        return;
      }
      const uuid = item.attachmentUUID || item.attachmentUuid || "";
      if (!uuid) return;
      attachmentFetchRef.current.add(id);
      getFilePreviewUrlList(uuid)
        .then(urls => {
          if (!Array.isArray(urls) || urls.length === 0) return;
          setPosts(prev =>
            prev.map(p =>
              String(p.id) === id
                ? { ...p, attachmentImageUrls: urls }
                : p,
            ),
          );
        })
        .catch(() => {});
    });
  }, [posts]);

  useEffect(() => {
    collections.forEach(item => {
      const id = String(item.id);
      if (!item.iconId || collectionIconFetchRef.current.has(id)) return;
      if (
        typeof item.collectionIcon === "string" &&
        item.collectionIcon.startsWith("http")
      ) {
        collectionIconFetchRef.current.add(id);
        return;
      }
      collectionIconFetchRef.current.add(id);
      getFilePreviewUrl(item.iconId)
        .then(url => {
          if (!url) return;
          setCollections(prev =>
            prev.map(c =>
              String(c.id) === id ? { ...c, collectionIcon: url } : c,
            ),
          );
        })
        .catch(() => {});
    });
  }, [collections]);

  const handleToggleFollow = async () => {
    const res = await post("/user/trace/saveOrCancel", {
      objectId: String(resolvedUserId ?? userId),
      traceUserId: Number(resolvedUserId ?? userId) || resolvedUserId,
      type: TRACE_TYPE_USER,
    });
    if (res?.success) {
      setFollowing(prev => !prev);
      setFollowersCount(prev => (following ? Math.max(0, prev - 1) : prev + 1));
    }
  };

  const openPostItem = item => {
    if (selectedFilter === USER_PROFILE_FILTER_GAMES) {
      openGameListItem(item);
      return;
    }
    if (selectedFilter === USER_PROFILE_FILTER_COLLECTIONS) {
      openCollectionPostItem(item);
      return;
    }
    openArticleItem(item);
  };

  const openGameListItem = item => {
    const gameId = firstLinkedGameId(item);
    if (gameId > 0) {
      onClose?.();
      navigate(gameDetailPath(gameId));
      return;
    }
    openArticleItem(item);
  };

  const openCollectionPostItem = item => {
    const collectionId = firstLinkedCollectionId(item);
    if (collectionId > 0) {
      onClose?.();
      navigate(collectionDetailPath(collectionId));
    } else {
      openArticleItem(item);
    }
  };

  const openArticleItem = item => {
    const postId = item?.id;
    if (postId != null && String(postId).trim() !== "") {
      onClose?.();
      navigate(postDetailPath(postId));
    }
  };

  const openCollectionItem = item => {
    const collectionId = item?.id;
    if (collectionId != null && String(collectionId).trim() !== "") {
      onClose?.();
      navigate(collectionDetailPath(collectionId));
    }
  };

  const getPostCover = item => {
    const fromAttachments = item?.attachmentImageUrls?.[0];
    if (fromAttachments) return fromAttachments;
    const fromPreview = item?.attachments?.[0]?.previewUrl;
    if (fromPreview) return fromPreview;
    return "";
  };

  const getPostTitle = item =>
    item?.title?.trim() ||
    firstLinkedGameName(item, "") ||
    t("userProfile.post.untitled", "Post");

  const renderPostCard = item => {
    const cover = getPostCover(item);
    const title = getPostTitle(item);
    const excerpt = stripHtml(item?.content || "");
    const likes = Number(item?.likeCnt || 0);

    return (
      <article
        key={item.id}
        className={styles.postCard}
        onClick={() => openPostItem(item)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPostItem(item);
          }
        }}
      >
        {cover ? (
          <img className={styles.postCover} src={cover} alt="" />
        ) : (
          <div className={styles.postCoverPlaceholder} aria-hidden="true" />
        )}
        <div className={styles.postBody}>
          <h4
            className={`${styles.postTitle} notranslate`}
            {...noAutoTranslateUserTextProps}
          >
            {title}
          </h4>
          {excerpt ? (
            <p
              className={`${styles.postExcerpt} notranslate`}
              {...noAutoTranslateUserTextProps}
            >
              {excerpt}
            </p>
          ) : null}
          <div className={styles.postFooter}>
            {avatarUrl ? (
              <img
                className={`${styles.postFooterAvatar} notranslate`}
                src={avatarUrl}
                alt=""
                {...noAutoTranslateUserTextProps}
              />
            ) : null}
            <span
              className={`${styles.postFooterName} notranslate`}
              {...noAutoTranslateUserTextProps}
            >
              {displayName ||
                t("userProfile.defaultName", "User")}
            </span>
            <span className={styles.postFooterLikes} aria-label={t("aria.like", "Like")}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {likes}
            </span>
          </div>
        </div>
      </article>
    );
  };

  const renderCollectionCard = item => {
    const name =
      item.collectionName?.trim() ||
      t("collection.untitled", "Untitled collection");
    const gameCount = Number(item.gameSize) || 0;
    const meta =
      gameCount > 0
        ? t("userProfile.collection.gameCount", "{count} games", {
            count: gameCount,
          })
        : item.collectionIntro?.trim() ||
          t("collection.noIntro", "No description");

    return (
      <article
        key={item.id}
        className={styles.collectionCard}
        onClick={() => openCollectionItem(item)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openCollectionItem(item);
          }
        }}
      >
        {item.collectionIcon ? (
          <img
            className={styles.collectionCover}
            src={item.collectionIcon}
            alt=""
          />
        ) : (
          <div className={styles.collectionCoverPlaceholder} aria-hidden="true" />
        )}
        <div className={styles.collectionMeta}>
          <h4
            className={`${styles.collectionName} notranslate`}
            {...noAutoTranslateUserTextProps}
          >
            {name}
          </h4>
          <p
            className={`${styles.collectionDesc} notranslate`}
            {...noAutoTranslateUserTextProps}
          >
            {meta}
          </p>
        </div>
      </article>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingState}>
          {t("userProfile.loading", "Loading...")}
        </div>
      );
    }

    if (selectedFilter === USER_PROFILE_FILTER_COLLECTIONS) {
      if (visibleCollections.length === 0) {
        return (
          <div className={styles.emptyState}>
            {t("userProfile.collections.empty", "No collections yet")}
          </div>
        );
      }
      return (
        <div className={styles.collectionGrid}>
          {visibleCollections.map(renderCollectionCard)}
        </div>
      );
    }

    if (filteredPosts.length === 0) {
      const emptyKey =
        selectedFilter === USER_PROFILE_FILTER_TOPICS
          ? "userProfile.topics.empty"
          : "userProfile.games.empty";
      const emptyFallback =
        selectedFilter === USER_PROFILE_FILTER_TOPICS
          ? "No topics yet"
          : "No games yet";
      return (
        <div className={styles.emptyState}>
          {t(emptyKey, emptyFallback)}
        </div>
      );
    }

    return (
      <div className={styles.postGrid}>{filteredPosts.map(renderPostCard)}</div>
    );
  };

  const panel = (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.panel}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("userProfile.title", "User profile")}
      >
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t("aria.close", "Close")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.scrollBody}>
          <div className={styles.profileHeader}>
            {avatarUrl ? (
              <img
                className={`${styles.avatar} notranslate`}
                src={avatarUrl}
                alt=""
                {...noAutoTranslateUserTextProps}
              />
            ) : (
              <span
                className={`${styles.avatarFallback} notranslate`}
                aria-hidden="true"
                {...noAutoTranslateUserTextProps}
              >
                {firstChar(displayName || fallbackName)}
              </span>
            )}

            <div className={styles.profileMeta}>
              <h2
                className={`${styles.displayName} notranslate`}
                {...noAutoTranslateUserTextProps}
              >
                {displayName ||
                  fallbackName ||
                  t("userProfile.defaultName", "User")}
              </h2>
              {introduction ? (
                <p
                  className={`${styles.introduction} notranslate`}
                  {...noAutoTranslateUserTextProps}
                >
                  {introduction}
                </p>
              ) : null}
            </div>

            {showFollowButton ? (
              <div className={styles.followSlot}>
                <FollowButton
                  following={!!following}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={onRequireLogin}
                  onClick={handleToggleFollow}
                />
              </div>
            ) : null}
          </div>

          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{followingCount}</span>
              <span className={styles.statLabel}>
                {t("userProfile.stat.following", "Following")}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{followersCount}</span>
              <span className={styles.statLabel}>
                {t("userProfile.stat.followers", "Followers")}
              </span>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>
            {t("userProfile.tab.posts", "Posts")}
          </h3>

          <div className={styles.filterRow}>
            {FILTER_OPTIONS.map(option => (
              <button
                key={option.id}
                type="button"
                className={`${styles.filterChip} ${
                  selectedFilter === option.id ? styles.filterChipActive : ""
                }`}
                onClick={() => setSelectedFilter(option.id)}
              >
                {t(option.labelKey, option.fallback)}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
};

export default UserProfilePanel;
